import { createApp } from "vue";
import App from "@/views/App.vue";
import router from "@/router";
import store from "@/store";
import langUtil, { StringKey } from "@/utils/lang.util";
import VueGtag from "vue-gtag-next";
import analyticsPlugin from "@/plugins/analytics.plugin";

// Vuescroll
import vuescroll from "vuescroll";
import "vuescroll/dist/vuescroll.css";

// Lottie animations
import { Vue3Lottie } from 'vue3-lottie';

import "./registerServiceWorker";

// Font awesome - Only import specific icons for better performance
import { library } from "@fortawesome/fontawesome-svg-core";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

// Add only the icons we actually use
library.add(faTimes);

// Cookie consent
import "cookieconsent/build/cookieconsent.min.css";
import "cookieconsent/build/cookieconsent.min.js";

// Axios interceptor
import createAxiosInstance from '@/services/axiosInterseptors';
import locationService from "./services/location.service";
import userUtil from "./utils/user.util";

const axiosInstance = createAxiosInstance(store);

declare global {
  interface Window {
    cookieconsent: any;
    VK_0?: any;
    gtag?: (...args: any[]) => void;
  }
}

const app = createApp(App);

// Register global components
app.component("font-awesome-icon", FontAwesomeIcon);
app.component("Vue3Lottie", Vue3Lottie);

// Use plugins
app.use(router);
app.use(store);
app.use(vuescroll);

// Configure Google Analytics
const gaTrackingId = import.meta.env.VITE_GA_TRACKING_ID;
if (gaTrackingId && gaTrackingId !== 'G-XXXXXXXXXX') {
  console.log('Google Analytics enabled', gaTrackingId);
  app.use(VueGtag, {
    property: {
      id: gaTrackingId
    }
  });
}


// Register analytics plugin for $ga compatibility
app.use(analyticsPlugin);

// Global properties
app.config.globalProperties.$http = axiosInstance;

// Mount app after initialization
(async () => {
  try {
    const { countryCode, languageCode } = await locationService.getLocationCountryCode();
    const browserLang = navigator.language?.split('-')[0];
    const mappedLangCode = langUtil.mapLanguage(browserLang, languageCode);
    userUtil.setCountryCode(countryCode);
    userUtil.setLanguageCode(mappedLangCode);
    app.mount('#app');
  } catch (error) {
    app.mount('#app');
  }
})();

// Cookie consent
const lang = navigator.language ? navigator.language.split("-")[0] : null;
try {
  if (window.VK_0) {
    // Empty block to detect vewd TV
  }
} catch (error) {
  window.cookieconsent.initialise({
    palette: {
      popup: {
        background: "#4f4f4f",
        text: "#ffffff",
      },
      button: {
        background: "#4fc3f7",
        text: "#ffffff",
      },
    },
    type: "opt-out",
    theme: "edgeless",
    content: {
      message: langUtil.getLang().t(StringKey.CmpText),
      dismiss: "Ok!",
      allow: langUtil.getLang().t(StringKey.CmpAllow),
      deny: langUtil.getLang().t(StringKey.CmpDeny),
      link: langUtil.getLang().t(StringKey.CmpMore),
      href:
        lang && ["fr", "es", "nl", "de", "it", "ar"].indexOf(lang) >= 0
          ? `https://www.kidjo.tv/${lang}/privacy`
          : "https://www.kidjo.tv/privacy",
    },
    cookie: {
      name: "cookieconsent_status",
    },
    onStatusChange() {
      const type = this.options.type;
      const didConsent = this.hasConsented();
      if (type === "opt-out" && !didConsent) {
        // Disable Google analytics
        if (window.gtag) {
          window.gtag('consent', 'update', {
            'analytics_storage': 'denied'
          });
        }
        window.cookieconsent.utils.setCookie("_ga", null, -1);
        window.cookieconsent.utils.setCookie("_gat", null, -1);
        window.cookieconsent.utils.setCookie("_gid", null, -1);
      }
    },
    law: {
      regionalLaw: false,
    }
  });
}
